/**
 * Error Utilities for AI Consultant
 *
 * Provides error classification, recovery strategies, and reporting utilities
 * for robust error handling throughout the AI Consultant feature.
 */

export type ErrorCategory =
  | "network"
  | "authentication"
  | "validation"
  | "platform"
  | "ai_service"
  | "rendering"
  | "unknown";

export type ErrorSeverity = "info" | "warning" | "error" | "critical";

export interface ErrorContext {
  category: ErrorCategory;
  severity: ErrorSeverity;
  recoverable: boolean;
  userMessage: string;
  technicalMessage: string;
  suggestedActions: string[];
  metadata?: Record<string, unknown>;
}

export interface ErrorRecoveryStrategy {
  canRecover: boolean;
  autoRetry: boolean;
  maxRetries?: number;
  retryDelay?: number; // milliseconds
  preserveState: boolean;
  fallbackAction?: () => void;
}

/**
 * Classifies an error and provides context for handling
 */
export function classifyError(error: unknown): ErrorContext {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const message = errorObj.message.toLowerCase();

  // Network errors
  if (
    message.includes("fetch") ||
    message.includes("network") ||
    message.includes("connection") ||
    message.includes("timeout") ||
    errorObj.name === "NetworkError"
  ) {
    return {
      category: "network",
      severity: "warning",
      recoverable: true,
      userMessage: "We're having trouble connecting. Your work is saved.",
      technicalMessage: errorObj.message,
      suggestedActions: [
        "Check your internet connection",
        "Retry the operation",
        "Wait a moment and try again"
      ],
      metadata: { originalError: errorObj.name }
    };
  }

  // Authentication errors
  if (
    message.includes("unauthorized") ||
    message.includes("authentication") ||
    message.includes("token") ||
    message.includes("session expired") ||
    errorObj.message.includes("401")
  ) {
    return {
      category: "authentication",
      severity: "error",
      recoverable: false,
      userMessage: "Your session has expired. Please sign in again.",
      technicalMessage: errorObj.message,
      suggestedActions: [
        "Refresh the page",
        "Sign in again",
        "Check your account status"
      ],
      metadata: { requiresAuth: true }
    };
  }

  // Platform/API errors
  if (
    message.includes("salesforce") ||
    message.includes("hubspot") ||
    message.includes("servicenow") ||
    message.includes("platform") ||
    message.includes("api error")
  ) {
    return {
      category: "platform",
      severity: "error",
      recoverable: true,
      userMessage: "We couldn't complete the platform operation. Your conversation is saved.",
      technicalMessage: errorObj.message,
      suggestedActions: [
        "Verify your platform connection",
        "Check platform permissions",
        "Try a simpler operation first",
        "Contact support if this persists"
      ],
      metadata: { platformError: true }
    };
  }

  // AI Service errors
  if (
    message.includes("openai") ||
    message.includes("ai service") ||
    message.includes("model") ||
    message.includes("rate limit") ||
    message.includes("quota")
  ) {
    return {
      category: "ai_service",
      severity: "warning",
      recoverable: true,
      userMessage: "The AI service is temporarily unavailable. We'll retry automatically.",
      technicalMessage: errorObj.message,
      suggestedActions: [
        "Wait a moment - we're retrying",
        "Simplify your request",
        "Try again in a few seconds"
      ],
      metadata: { aiError: true }
    };
  }

  // Rendering/UI errors
  if (
    message.includes("render") ||
    message.includes("component") ||
    message.includes("jsx") ||
    message.includes("react") ||
    message.includes("webgl")
  ) {
    return {
      category: "rendering",
      severity: "error",
      recoverable: true,
      userMessage: "We had trouble displaying this content. The data is safe.",
      technicalMessage: errorObj.message,
      suggestedActions: [
        "Refresh the page",
        "Try a different browser",
        "Check browser extensions",
        "View in simplified mode"
      ],
      metadata: { renderError: true }
    };
  }

  // Validation errors
  if (
    message.includes("validation") ||
    message.includes("invalid") ||
    message.includes("required") ||
    message.includes("missing")
  ) {
    return {
      category: "validation",
      severity: "info",
      recoverable: true,
      userMessage: "Please check your input and try again.",
      technicalMessage: errorObj.message,
      suggestedActions: [
        "Review your input",
        "Fill in all required fields",
        "Check for typos"
      ],
      metadata: { validationError: true }
    };
  }

  // Unknown errors
  return {
    category: "unknown",
    severity: "error",
    recoverable: true,
    userMessage: "Something unexpected happened. Your work is saved.",
    technicalMessage: errorObj.message,
    suggestedActions: [
      "Refresh the page",
      "Try again",
      "Contact support if this persists"
    ],
    metadata: { unknownError: true }
  };
}

/**
 * Determines the recovery strategy for an error
 */
export function getRecoveryStrategy(errorContext: ErrorContext): ErrorRecoveryStrategy {
  switch (errorContext.category) {
    case "network":
    case "ai_service":
      return {
        canRecover: true,
        autoRetry: true,
        maxRetries: 3,
        retryDelay: 2000,
        preserveState: true
      };

    case "platform":
      return {
        canRecover: true,
        autoRetry: false,
        preserveState: true
      };

    case "validation":
      return {
        canRecover: true,
        autoRetry: false,
        preserveState: true
      };

    case "rendering":
      return {
        canRecover: true,
        autoRetry: false,
        preserveState: false
      };

    case "authentication":
      return {
        canRecover: false,
        autoRetry: false,
        preserveState: false
      };

    default:
      return {
        canRecover: true,
        autoRetry: false,
        preserveState: true
      };
  }
}

/**
 * Sanitizes error for display (removes sensitive info)
 */
export function sanitizeErrorForDisplay(error: unknown): string {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  let message = errorObj.message;

  // Remove sensitive patterns
  message = message.replace(/token[=:]\s*[\w-]+/gi, "token=***");
  message = message.replace(/key[=:]\s*[\w-]+/gi, "key=***");
  message = message.replace(/password[=:]\s*\S+/gi, "password=***");
  message = message.replace(/authorization:\s*\S+/gi, "authorization: ***");

  return message;
}

/**
 * Creates a user-friendly error message
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  const errorContext = classifyError(error);
  return errorContext.userMessage;
}

/**
 * Checks if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const errorContext = classifyError(error);
  const strategy = getRecoveryStrategy(errorContext);
  return strategy.canRecover && strategy.autoRetry;
}

/**
 * Delay utility for retry logic
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry wrapper with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    onRetry?: (attempt: number, error: unknown) => void;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    onRetry
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if not retryable or if this was the last attempt
      if (!isRetryableError(error) || attempt === maxRetries) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delayMs = Math.min(
        initialDelay * Math.pow(2, attempt),
        maxDelay
      );

      onRetry?.(attempt + 1, error);
      await delay(delayMs);
    }
  }

  throw lastError;
}

/**
 * Safe JSON parsing with error handling
 */
export function safeJsonParse<T = unknown>(
  json: string,
  fallback: T
): T {
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}
